import { useEffect } from 'react';
import styles from './Account.module.css';
import { formatDate } from '../../services/billingService';

/**
 * Single, plain-language confirmation. No support email, no multi-step funnel -
 * one clear "Yes, cancel" action and an easy way out. Access continues until the
 * end of the trial/paid period, which we state explicitly.
 */
export default function CancelSubscriptionModal({ open, billing, busy, onConfirm, onClose }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const sub = billing?.subscription;
  const trial = billing?.trial;
  const onTrial = trial?.status === 'pending' || trial?.status === 'active';
  // Which date ends their access. A trial date is only right for someone still on the trial:
  // this read `trial?.endsAt || sub?.nextBillingDate`, preferring the trial end even for a
  // paying customer, so someone on a live Solo plan running to Oct 30 was told it ended on
  // Sep 18 - a date already in the past by the time they read it.
  const endsOn = onTrial
    ? (trial?.endsAt || sub?.periodEnd)
    : (sub?.periodEnd || sub?.cancelsAt || sub?.nextBillingDate);

  return (
    <div className={styles.overlay} onClick={onClose} role="dialog" aria-modal="true" aria-label="Cancel subscription">
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalTitle}>Cancel subscription?</div>
        <div className={styles.modalBody}>
          {onTrial ? (
            <>
              You're on a free trial, so <b>you haven't been charged</b> and you won't be. Your{' '}
              <b>{sub?.plan}</b> plan stays active until <b>{formatDate(endsOn)}</b>, then simply ends.
              You can resubscribe any time - no email or support ticket required.
            </>
          ) : (
            <>
              Your <b>{sub?.plan}</b> plan will stay active until <b>{formatDate(endsOn)}</b>. You
              won't be charged again, and you keep full access until then. You can resubscribe any
              time - no email or support ticket required.
            </>
          )}
        </div>
        <div className={styles.modalActions}>
          <button className={styles.btnGhost} onClick={onClose} disabled={busy}>
            Keep my plan
          </button>
          <button className={styles.btnDangerSolid} onClick={onConfirm} disabled={busy}>
            {busy ? 'Canceling…' : 'Yes, cancel subscription'}
          </button>
        </div>
      </div>
    </div>
  );
}
